/**
 * RT ROI Identification Codes (3006,0086) the application can assign to
 * structures. This is the write-side registry — reading and displaying an
 * arbitrary code from a loaded RTSTRUCT is already generic
 * ({@link DicomRoi.identificationCode}, StructureInformation).
 *
 * Codes are data, not per-feature constants: a new coded structure is a new
 * registry entry, not a new module. Mutations go through
 * {@link RoiService.setIdentificationCode} (tracked, approval-guarded).
 */
import { RoiIdentificationCodeSequence } from './dicomstructureset';

/**
 * Varian 99VMS structure-code scheme. The designator/version mirror the
 * values MVision's mvdicom optimization writer uses for 99VMS_STRUCTCODE
 * codes, so structures we generate map onto TPS templates the same way
 * across products.
 */
const VMS_STRUCTCODE_SCHEME = {
    codingSchemeDesignator: '99VMS_STRUCTCODE',
    codingSchemeVersion: '1.0',
} as const;

/** Builds a Varian 99VMS structure code entry. */
export function vmsStructCode(
    codeValue: string,
    codeMeaning: string = codeValue
): RoiIdentificationCodeSequence {
    return { codeValue, codeMeaning, ...VMS_STRUCTCODE_SCHEME };
}

/**
 * Known structure codes assigned by application features. Extend here —
 * one entry per coded structure, keyed by clinical concept.
 */
export const STRUCTURE_CODES = {
    /** The patient outline (EXTERNAL / body) — SR-WOR-H-27-1. */
    body: vmsStructCode('BODY'),
    /** A couch-model component (shell, interior, ...) — SUPPORT type. One
     *  code for every component of every library entry: the shape/density
     *  differences live in the couch library, not in the coded identity.
     *  Spelled as Varian's own planning system writes it on its couch
     *  structures — `Support`, not `SUPPORT`: a code value is compared
     *  case-sensitively, and the interpreted type's upper case does not
     *  carry over. */
    couchSupport: vmsStructCode('Support'),
} as const;
